import React, { useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
    CircularProgress,
    Chip,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import request from "../../utils/request";
import loader from "../common/loader.gif";
import { LoadingButton } from "@mui/lab";

type Contact = {
    id: number;
    branchId: number;
    department: string | null;
    firstName: string;
    lastName: string;
    email: string;
};

export default function ShareToInsurer({ endoresementData, onClose, setShowSnackBarAlert }) {
    const [loading, setLoading] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [loadingMain, setLoadingMain] = useState(false);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [branchId, setBranchId] = useState<number | null>(null);
    const [policyDetails, setPolicyDetailData] = useState(null);
    const [useCustomEmail, setUseCustomEmail] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch Policy Details
    useEffect(() => {
        fetchPolicyDetails();
    }, []);

    useEffect(() => {
        if (!branchId) return;
        fetchBranchContactList();
    }, [branchId]);

    const fetchPolicyDetails = () => {
        setLoadingMain(true);
        request({
            url: "insurancePolicy/getPolicyById",
            method: "post",
            data: {
                policyId: endoresementData?.policyId,
            },
        }).then(
            (response) => {
                setBranchId(response["data"]?.insuranceCompanyBranch?.id ?? null);
                setPolicyDetailData(response["data"])
                setLoadingMain(false);
            },
            (error) => {
                console.log(error);
                setLoadingMain(false);
            }
        );
    };

    const fetchBranchContactList = () => {
        setLoading(true);
        setError(null);

        const payload = {
            search: "",
            orderColumn: "id",
            orderDirection: "DESC",
            offset: 0,
            limit: 10000,
            branchId,
        };

        request({
            url: "icb-contacts/getIcbContactList",
            method: "POST",
            data: payload,
        })
            .then((response) => {
                setContacts(response["contactData"] ?? []);
            })
            .catch((err: any) => {
                setError(err?.message || "Failed to load contacts");
            })
            .finally(() => setLoading(false));
    };

    // Formik Setup
    const formik = useFormik({
        initialValues: {
            email: "",
            contact: null as Contact | null,
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .transform((value) => value?.trim())
                .test(
                    'multiple-emails',
                    'Invalid Email ID',
                    (value) => {
                        if (!value) return true; // handled by required()
                        const emails = value
                            .split(',')
                            .map(e => e.trim())
                            .filter(Boolean);

                        return emails.every(email =>
                            Yup.string().email().isValidSync(email)
                        );
                    }
                )
                .when([], {
                    is: () => useCustomEmail,
                    then: (schema) => schema.required('Email is required'),
                }),

            contact: Yup.mixed().when([], {
                is: () => !useCustomEmail,
                then: (schema) => schema.required("Please select a contact"),
            }),
        }),
        onSubmit: (values) => {
            setLoadingSubmit(true);

            request({
                url: '/endorsement/shareEndorsementToInsurer',
                method: 'post',
                data: {
                    ids: [endoresementData.id],
                    type: 'ShareEndorsement',
                    insuranceCompanyId: policyDetails?.insuranceCompany?.id,
                    insuranceCompanyBranchId: branchId,
                    insuranceCompanyBranchContact: !useCustomEmail ? values.contact?.id : null,
                    otherContact: useCustomEmail ? values.email.trim() : null
                }
            }) // eslint-disable-next-line
                .then(
                    (response) => {
                        setLoadingSubmit(false);
                        setShowSnackBarAlert({
                            status: true,
                            type: 'success',
                            message: 'Email Sent Successfully'
                        });
                        formik.resetForm();
                        onClose();

                    },
                    (error) => {
                        setLoadingSubmit(false);
                        setShowSnackBarAlert({
                            status: true,
                            type: 'error',
                            message: 'Something went wrong'
                        });
                        console.log(error);
                        formik.resetForm();
                        onClose();
                    }
                );


        },
    });

    const options = useMemo(
        () =>
            contacts.sort((a, b) =>
                a.firstName.localeCompare(b.firstName, undefined, {
                    sensitivity: "base",
                })
            ),
        [contacts]
    );

    return (
        <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Select Contact Email</DialogTitle>
            <DialogContent dividers>
                {loadingMain ? (
                    <div style={{ textAlign: "center" }}>
                        <img
                            alt="loader"
                            src={loader}
                            className="loader"
                            width={100}
                            height={100}

                        />
                    </div>

                ) : (
                    <form onSubmit={formik.handleSubmit}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useCustomEmail}
                                    onChange={(e) => {
                                        setUseCustomEmail(e.target.checked);
                                        formik.setFieldValue("email", "");
                                        formik.setFieldValue("contact", null);
                                    }}
                                />
                            }
                            label="Send to a Different Email ID"
                        />

                        {useCustomEmail ? (
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Email ID"
                                name="email"
                                size="small"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helperText={formik.touched.email && formik.errors.email}
                            />
                        ) : (
                            <Box mt={2}>
                                <Autocomplete<Contact, false, false, false>
                                    size="small"
                                    options={options}
                                    loading={loading}
                                    value={formik.values.contact}
                                    disabled={error == 'Request failed with status code 403'}
                                    onChange={(_, newValue) =>
                                        formik.setFieldValue("contact", newValue)
                                    }
                                    getOptionLabel={(o) =>
                                        `${o.firstName ?? ""} ${o.lastName ?? ""} (${o.email})`.trim()
                                    }
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Branch Contact"
                                            placeholder="Select a contact"

                                            error={formik.touched.contact && Boolean(formik.errors.contact)}
                                            helperText={formik.touched.contact && formik.errors.contact}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loading ? <CircularProgress size={18} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    noOptionsText={loading ? "Loading..." : "No contacts found"}
                                />
                            </Box>
                        )}

                        {error && (
                            <Box mt={2} color="error.main" fontSize={14}>
                                {error == 'Request failed with status code 403' ? <>
                                    <Chip
                                        label="You don't have permission to view the Branch Contact."
                                        color="error"
                                        variant="outlined"
                                    />
                                </>:error}
                            </Box>
                        )}

                        <div style={{ marginTop: "10px", textAlign: "right" }}>
                            <Button onClick={onClose} variant="contained" color="error">
                                Cancel
                            </Button>
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                loading={formik.isSubmitting}
                                style={{ marginLeft: "10px" }}
                            >
                                Send
                            </LoadingButton>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
